import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { login, error, clearError, isAuthenticated, loading } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    
    // Check for verification success message from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    if (verified === 'true') {
      setFormError('');
      // Show success message instead of error
      const successElement = document.createElement('div');
      successElement.className = 'success-message';
      successElement.innerHTML = '<i class="fas fa-check-circle"></i> ¡Correo electrónico verificado exitosamente! Por favor inicie sesión.';
      
      // Find error message element and replace or insert before form
      const errorElement = document.querySelector('.error-message');
      if (errorElement) {
        errorElement.parentNode.replaceChild(successElement, errorElement);
      } else {
        const formElement = document.querySelector('form');
        formElement.parentNode.insertBefore(successElement, formElement);
      }
    }
  }, [isAuthenticated, navigate]);

  // Clear auth context error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Update local error state when auth context error changes
  useEffect(() => {
    if (error) {
      // Traducir mensajes de error comunes del inglés al español
      if (error === 'Invalid credentials') {
        setFormError('Credenciales inválidas');
      } else if (error === 'Please provide email and password') {
        setFormError('Por favor proporcione correo electrónico y contraseña');
      } else if (error === 'Session expired. Please login again.') {
        setFormError('Sesión expirada. Por favor inicie sesión nuevamente.');
      } else {
        setFormError(error);
      }
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validate form
    if (!email || !password) {
      setFormError('Por favor complete todos los campos');
      return;
    }
    
    // Attempt login
    console.log('Attempting login with:', email);
    const result = await login(email, password);
    console.log('Login result:', result);
    
    if (result && result.needsVerification) {
      console.log('User needs verification, redirecting to OTP page');
      // Store email in localStorage before redirecting
      localStorage.setItem('pendingVerificationEmail', email);
      // Redirect to verification page if user needs to verify email
      navigate('/verify-otp', { state: { email } });
    } else if (result && result.success) {
      console.log('Login successful, redirecting to dashboard');
      // La redirección basada en roles se maneja en el componente ProtectedRoute
      // que ya verifica el rol del usuario a través del contexto de autenticación
      navigate('/dashboard');
    } else {
      console.log('Login failed but no specific error condition matched');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-form-container">
        <div className="auth-header">
          <h2>Bienvenido a Ventaz Web</h2>
          <p>Tu asistente diario: Registra y resume tus ventas fácilmente y desde tu celular</p>
        </div>
        
        {formError && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {formError}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Correo Electrónico</label>
            <div className="input-with-icon">
              <i className="fas fa-envelope input-icon"></i>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="Ingrese su correo electrónico"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor complete este campo')}
                onInput={(e) => e.target.setCustomValidity('')}
                autoComplete="username"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="input-with-icon">
              <i className="fas fa-lock input-icon"></i>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="Ingrese su contraseña"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor ingrese su contraseña')}
                onInput={(e) => e.target.setCustomValidity('')}
                autoComplete="current-password"
              />
            </div>
          </div>
          
          <div className="form-options">
            <div className="remember-me">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                disabled={loading}
              />
              <label htmlFor="remember-me">Recordarme</label>
            </div>
            <Link to="/forgot-password" className="forgot-password">
              ¿Olvidó su contraseña?
            </Link>
          </div>
          
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span> Iniciando sesión...
              </>
            ) : (
              'Iniciar Sesión'
            )}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            ¿No tiene una cuenta? <Link to="/register">Crear Cuenta</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
